export enum ScenarioCsvHeaders {
  Name = 'Name',
  ID = 'ID',
  Status = 'Status',
  Sources = 'Sources',
  Events = 'Events',
  Consequences = 'Consequences',
  RiskOwner = 'Risk Owner',
  RiskNarrative = 'Risk Narrative',
  UnmodifiedFrequencyTimes = 'Unmodified Frequency - Times',
  UnmodifiedFrequencyYears = 'Unmodified Frequency - Years',
  LiveFrequencyTimes = 'Live Frequency - Times',
  LiveFrequencyYears = 'Live Frequency - Years',
  ProjectedFrequencyTimes = 'Projected Frequency - Times',
  ProjectedFrequencyYears = 'Projected Frequency - Years',
  FrequencyNote = 'Frequency - Note',
  UnmodifiedConsequenceLower = 'Unmodified Consequence - Lower',
  UnmodifiedConsequenceUpper = 'Unmodified Consequence - Upper',
  ConsequenceLowerFormula = 'Consequence - Lower Formula',
  ConsequenceUpperFormula = 'Consequence - Upper Formula',
  LiveConsequenceLower = 'Live Consequence - Lower',
  LiveConsequenceUpper = 'Live Consequence - Upper',
  ProjectedConsequenceLower = 'Projected Consequence - Lower',
  ProjectedConsequenceUpper = 'Projected Consequence - Upper',
  ConsequenceNote = 'Consequence - Note',
  IdentifiedDate = 'Identified Date (DD-MM-YYYY)',
  ReviewDate = 'Review Date (DD-MM-YYYY)',
  RiskManagementStrategy = 'Risk Management Strategy',
  RiskManagementNote = 'Risk Management Note',
  Created = 'Created (DD-MM-YYYY HH:mm)',
  Updated = 'Updated (DD-MM-YYYY HH:mm)',
}

export enum ScenarioPackCsvHeaders {
  PackID = 'Scenario Pack ID',
  PackName = 'Scenario Pack Name',
  PackDescription = 'Scenario Pack Description',
  PackAuthor = 'Scenario Pack Author',
  PackAuthorLogoPath = 'Scenario Pack Author Logo Path',
  PackCategory = 'Scenario Pack Category',
  PackIsSystem = 'Scenario Pack Is System',
  ScenarioDescription = 'Scenario Description',
}

export enum ControlCsvHeaders {
  ID = 'ID',
  Name = 'Name',
  Strategy = 'Strategy',
  Description = 'Description',
  Owner = 'Owner',
  Status = 'Status',
  ExpectedLiveDate = 'Expected Live Date (DD-MM-YYYY)',
  ActualLiveDate = 'Actual Live Date (DD-MM-YYYY)',
  ReviewDate = 'Review Date (DD-MM-YYYY)',
  AnnualCost = 'Annual Cost',
  ImplementationCost = 'Implementation Cost',
  CostNote = 'Cost Note',
  Vendor = 'Vendor',
  Source = 'Source URL',
  UrlName = 'URL Name',
  PolicyUrl = 'Policy URL',
  PolicyUrlText = 'Policy URL Text',
  ExistingMaturity = 'Existing Maturity',
  TargetMaturity = 'Target Maturity',
  Created = 'Created (DD-MM-YYYY HH:mm)',
  Updated = 'Updated (DD-MM-YYYY HH:mm)',
  Framework = 'Framework',
  FrameworkControlID = 'Framework Control ID',
}

/** Cost + policy columns post-date the original template — imports without them stay valid. */
export const OPTIONAL_CONTROL_CSV_HEADERS: string[] = [
  ControlCsvHeaders.ImplementationCost,
  ControlCsvHeaders.CostNote,
  ControlCsvHeaders.PolicyUrl,
  ControlCsvHeaders.PolicyUrlText,
];

export enum FrameworkControlCsvHeaders {
  FrameworkID = 'Framework ID',
  FrameworkIdentifier = 'Framework Identifier',
  FrameworkName = 'Framework Name',
  FrameworkShortName = 'Framework Short Name',
  FrameworkAuthor = 'Framework Author',
  FrameworkVersion = 'Framework Version',
  FrameworkPublishDate = 'Framework Publish Date (DD-MM-YYYY)',
  FrameworkDescription = 'Framework Description',
  ID = 'Framework Control Row ID',
  FrameworkControlID = 'Framework Control ID',
  ControlName = 'Control Name',
  ShortName = 'Short Name',
  Strategy = 'Strategy',
  Description = 'Description',
  // Same strings as ControlCsvHeaders so the RMP and framework sheet formats stay
  // consistent. Optional columns: absent ⇒ template fields untouched on reimport.
  AnnualCost = 'Annual Cost',
  ImplementationCost = 'Implementation Cost',
  CostNote = 'Cost Note',
}

export enum IncidentCsvHeaders {
  ID = 'ID',
  RefID = 'Ref ID',
  Name = 'Name',
  Description = 'Description',
  Source = 'Source URL',
  Date = 'Date of Incident (DD-MM-YYYY)',
  Owner = 'Owner',
  Impact = 'Impact',
  FinancialImpactUpper = 'Financial Impact Upper',
  FinancialImpactLower = 'Financial Impact Lower',
  Type = 'Type (Incident/Near Miss)',
  Severity = 'Severity',
}

export enum EventCsvHeaders {
  ID = 'ID',
  Owner = 'Owner',
  Title = 'Title',
  CompletionStatus = 'Completion Status',
  DueDate = 'Due Date (DD-MM-YYYY)',
  DateCompleted = 'Date Completed (DD-MM-YYYY)',
  CompletedBy = 'Completed By',
  Created = 'Date Created (DD-MM-YYYY HH:mm)',
}

export enum TableIds {
  default = 'tid:default',
  scenarioLib = 'tid:scenarioLib',
  scenarioLibGroup = 'tid:scenarioLibGroup',
  addScenarioScenarioLib = 'tid:addScenarioScenarioLib',
  assessmentScenarios = 'tid:assessmentScenarios',
  assessmentScenariosControls = 'tid:assessmentScenariosControls',
  scenarioControls = 'tid:scenarioControls',
  scenarioIncidents = 'tid:scenarioIncidents',
  rmp = 'tid:rmp',
  reportCentre = 'tid:reportCentre',
  rmpScenarios = 'tid:rmpScenarios',
  rmpFromFramework = 'tid:rmpFromFramework',
  addControlControlLib = 'tid:addControlControlLib',
  controlLib = 'tid:controlLib',
  controlLibFramework = 'tid:controlLibFrameworkV3',
  rmpMultiSelectModal = 'tid:rmpMultiSelectModal',
  controlScenarios = 'tid:controlScenarios',
  incidents = 'tid:incidents',
  incidentsScenarios = 'tid:incidentsScenarios',
  incidentSecnarios = 'tid:incidentSecnarios',
  wsMembers = 'tid:wsMembers',
  wsRequests = 'tid:wsRequests',
  wsInvites = 'tid:wsInvites',
  wsPeople = 'tid:wsPeople',
  orgMembers = 'tid:orgMembers',
  orgRequests = 'tid:orgRequests',
  orgInvites = 'tid:orgInvites',
  prAssessmentScenarios = 'tid:prAssessmentScenarios',
  prScenarioScenarios = 'tid:prScenarioScenarios',
  prAssessmentScenariosControls = 'tid:prAssessmentScenariosControls',
  scenarioLinkExistingControl = 'tid:scenarioLinkExistingControl',
  scenarioLinkSuggestedControl = 'tid:scenarioLinkSuggestedControl',
  scenarioLinkExistingIncident = 'tid:scenarioLinkExistingIncident',
  controlLinkScenarioAssessment = 'tid:controlLinkScenarioAssessment',
  controlLinkScenarioScenario = 'tid:controlLinkScenarioScenario',
  incidentLinkScenarioAssessment = 'tid:incidentLinkScenarioAssessment',
  incidentLinkScenarioScenario = 'tid:incidentLinkScenarioScenario',
  auditWorkspace = 'tid:auditWorkspace',
  auditAssessment = 'tid:auditAssessment',
  auditScenario = 'tid:auditScenario',
  auditUser = 'tid:auditUser',
  auditRmp = 'tid:auditRmp',
  auditControl = 'tid:auditControl',
  auditIncidents = 'tid:auditIncidents',
  auditIncident = 'tid:auditIncident',
  auditReport = 'tid:auditReport',
  assessmentVersions = 'tid:assessmentVersions',
  compareAssessments = 'tid:compareAssessments',
  compareAssessmentsV1 = 'tid:compareAssessmentsV1',
  compareAssessmentsV2 = 'tid:compareAssessmentsV2',
  variablesWorkspace = 'tid:variablesWorkspace',
  wizzardOrgModalAddControls = 'tid:wizzardOrgModalAddControls',
  wizardRecomendedControls = 'tid:wizardRecomendedControlsV2',
  wizardRecomendedControlsExpandContent = 'tid:wizardRecomendedControlsExpandContentV2',
  recommenderSuggestedControls = 'tid:recommenderSuggestedControls',
  recommenderSuggestedControlScenarios = 'tid:recommenderSuggestedControlScenarios',
  events = 'tid:events',
  suggestFrameworks = 'tid:suggestFrameworks',
  suggestControls = 'tid:suggestControls',
  linkFrameworksToRMPModal = 'tid:linkFrameworksToRMPModal',
  rootHerarchy = 'tid:rootHerarchy',
  accessTable = 'tid:accessTable',
}
